import type { ReactNode } from 'react'

// Single dismissible green flash bar, sits between the tab bar and layout
// (spec §9.4). Every mock action (auto-schedule placement, tray fallback,
// distribute/optimize summary, publish, AI-mock convert/revert/link, etc.)
// posts a message here; only one note is shown at a time.

export function FlashNote(props: {
  message: string | null
  onDismiss: () => void
}): ReactNode {
  const { message, onDismiss } = props

  // The role="status" container stays mounted even while there is no message:
  // aria-live regions only announce content *changes*, so mounting the region
  // together with its first message would never be read by screen readers.
  //
  // Named (like the schedule's "Placement mode" status banner) so it stays
  // distinguishable from other status live regions in both queries and SR
  // announcements — CardBoard's DndContext mounts its own unnamed
  // role="status" announcer wherever this renders under it.
  return (
    <div
      role="status"
      aria-label="Curriculum updates"
      style={
        message
          ? {
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background:
                'color-mix(in oklab, var(--cd-green) 7%, transparent)',
              border:
                '1px solid color-mix(in oklab, var(--cd-green) 30%, transparent)',
              borderRadius: '10px',
              padding: '8px 14px',
            }
          : undefined
      }
    >
      {message ? (
        <>
          <span
            style={{
              flex: 1,
              fontSize: '12.5px',
              color: 'var(--cd-green-soft)',
            }}
          >
            {message}
          </span>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="hover:opacity-70"
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: '12px',
              color: 'var(--cd-text-faint)',
            }}
          >
            ✕
          </button>
        </>
      ) : null}
    </div>
  )
}
